import * as React from 'react';

import styled, { css } from 'styled-components';

import Typography from '../../basics/typography';
import { hasEmoji, removeEmoji } from '@webapp/utils/validators';

export interface TextareaWithCountProps {
  children?: React.ReactChild;
  className?: string;
  locked?: boolean;
  focussed?: boolean;
  value?: string;
  maxLength?: number;
  error?: string;
  label?: React.ReactNode;
  duration?: string;
  autoComplete?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  isGrey?: string;
  onChange?: (
    value: string,
    ev?: React.ChangeEvent<HTMLTextAreaElement>
  ) => void;

  onFocus?: () => void;
  onBlur?: () => void;
}

interface TextareaState {
  active: boolean;
}

export class TextareaWithCount extends React.Component<TextareaWithCountProps> {
  state: TextareaState;

  constructor(props: TextareaWithCountProps) {
    super(props);

    this.state = {
      active: false,
    };
  }

  changeValue(event: React.ChangeEvent<HTMLTextAreaElement>) {
    let value = event.target.value;

    if (hasEmoji(value)) {
      value = removeEmoji(value);
    }

    this.setState({
      active: true,
    });
    if (
      value.length - 1 >=
      (this.props.maxLength ? this.props.maxLength : 300)
    ) {
      return;
    }
    if (this.props.onChange) {
      this.props.onChange(value, event);
    }
  }

  handleFocus() {
    if (!this.props.locked) {
      this.setState({ active: true });
      if (this.props.onFocus) {
        this.props.onFocus();
      }
    }
  }

  handleBlur() {
    if (!this.props.locked) {
      this.setState({ active: false });
      if (this.props.onBlur) {
        this.props.onBlur();
      }
    }
  }

  render() {
    const { active } = this.state;

    return (
      <StyledWrapper
        className={this.props.className}
        active={active}
        value={this.props.value}
        locked={this.props.locked}
      >
        <StyledTextarea
          className="textarea"
          id={this.props.id}
          value={this.props.value}
          active={active || this.props.value}
          autoComplete={this.props.autoComplete}
          onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) =>
            this.changeValue(ev)
          }
          onFocus={() => this.handleFocus()}
          onBlur={() => this.handleBlur()}
        />
        {this.props.error && !this.props.locked ? (
          <StyledError>{this.props.error}</StyledError>
        ) : null}
        <StyledLabel
          label={this.props.label}
          id={this.props.id}
          active={active || this.props.value}
          duration={this.props.duration}
        />
        <StyledLabelCount
          label={this.props.value?.length + '/' + this.props.maxLength}
          id={this.props.id}
          active={active || this.props.value}
          duration={this.props.duration}
        />
      </StyledWrapper>
    );
  }
}

export default TextareaWithCount;

const StyledWrapper = styled((props: any) => (
  <div className={props.className}>{props.children}</div>
))`
  width: auto;
  border-radius: 4px;
  position: relative;
  height: ${(props) => (props.active ? '228px' : '48px')};
  padding: ${(props) =>
    props.active || props.value ? '23px 12px 9px 9px' : '17px 12px 17px 9px'};
  background-color: ${({ theme }) => theme.colors.inputBackground};
  transition: height
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    padding
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  border-left: ${(props) =>
    props.error && !props.locked
      ? css`3px solid ${({ theme }) => theme.colors.error}`
      : '3px solid transparent'};

  ${(props) =>
    props.isGrey
      ? css`
          background-color: ${({ theme }) =>
            theme.colors.inputBackgroundAlternate};
        `
      : null}

  @media ${({ theme }) => theme.device.tablet} {
    height: ${(props) => (props.active ? '190px' : '60px')};
    padding: ${(props) =>
      props.active || props.value
        ? '28px 18px 14px 15px'
        : '21px 18px 21px 15px'};
  }

  .textarea {
    height: 100%;
  }

  ${(props) =>
    props.locked
      ? css`
          cursor: not-allowed;

          &:hover {
            box-shadow: ${({ theme }) => theme.colors.shadow};
          }

          input {
            pointer-events: none;
            opacity: 0.6;
          }

          label {
            color: ${({ theme }) => theme.colors.primary};
          }
        `
      : null}
`;

const StyledLabel = styled((props: any) => (
  <label htmlFor={props.id} className={props.className}>
    {props.label}
  </label>
))`
  position: absolute;
  top: ${(props) => (props.active ? '7px' : '15px')};
  left: 9px;
  font-size: ${(props) =>
    props.active
      ? css`
          ${({ theme }) => theme.typography.span}
        `
      : css`
          ${({ theme }) => theme.typography.span}
        `};
  font-weight: ${(props) => (props.active ? '400' : '700')};
  line-height: ${(props) => (props.active ? 'normal' : '24px')};
  color: ${(props) =>
    props.active
      ? css`
          ${({ theme }) => theme.colors.inputLabel}
        `
      : css`
          ${({ theme }) => theme.colors.primary}
        `};
  opacity: 1;
  pointer-events: none;
  transition: top
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    font-weight
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    font-size
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    line-height
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  transition-duration: ${(props) =>
    props.duration ||
    css`
      ${({ theme }) => theme.transitions.duration}
    `};

  @media ${({ theme }) => theme.device.tablet} {
    top: ${(props) => (props.active ? '12px' : '19px')};
    left: 15px;
    font-size: ${(props) =>
      props.active
        ? css`
            ${({ theme }) => theme.typography.span};
          `
        : css`
            ${({ theme }) => theme.typography.h4}
          `};
  }
`;

const StyledLabelCount = styled((props: any) => (
  <label htmlFor={props.id} className={props.className}>
    {props.label}
  </label>
))`
  position: absolute;
  top: ${(props) => (props.active ? '7px' : '15px')};
  right: 20px;
  font-size: ${({ theme }) => theme.typography.span};
  font-weight: ${(props) => (props.active ? '400' : '700')};
  line-height: ${(props) => (props.active ? 'normal' : '24px')};
  color: ${(props) =>
    props.active
      ? css`
          ${({ theme }) => theme.colors.inputLabel}
        `
      : css`
          ${({ theme }) => theme.colors.primary}
        `};
  opacity: 1;
  pointer-events: none;
  transition: top
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    font-weight
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    font-size
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`},
    line-height
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  transition-duration: ${(props) =>
    props.duration ||
    css`
      ${({ theme }) => theme.transitions.duration}
    `};

  @media ${({ theme }) => theme.device.tablet} {
    top: ${(props) => (props.active ? '12px' : '19px')};
    right: 20px;
    font-size: ${(props) =>
      props.active
        ? css`
            ${({ theme }) => theme.typography.span};
          `
        : css`
            ${({ theme }) => theme.typography.h4}
          `};
  }
`;

const StyledTextarea = styled((props: any) => (
  <textarea
    ref={props.ref}
    className={props.className}
    id={props.id}
    value={props.value}
    autoComplete={props.autoComplete}
    onChange={(ev: React.ChangeEvent<HTMLTextAreaElement>) =>
      props.onChange(ev)
    }
    onFocus={() => props.onFocus()}
    onBlur={() => props.onBlur()}
    rows={4}
  ></textarea>
))`
  width: 100%;
  position: relative;
  border: none;
  font-size: ${({ theme }) => theme.typography.h3};
  font-weight: 700;
  line-height: 16px;
  background-color: ${({ theme }) => theme.colors.inputBackground};
  color: ${({ theme }) => theme.colors.primary};
  cursor: auto;
  -webkit-appearance: none;
  resize: none;
  box-sizing: inherit;
  padding: 0;
  transition: height
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  @media ${({ theme }) => theme.device.tablet} {
    font-size: ${({ theme }) => theme.typography.h4};
    line-height: 16px;
  }
`;

const StyledError = styled(Typography).attrs(() => ({
  type: 'small',
  weight: '700',
}))`
  color: ${({ theme }) => theme.colors.error};
  position: absolute;
  top: 14px;
  right: 18px;
  text-align: right;
`;
